import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react';
import {
  Box,
  Flex,
  List,
  ListItem,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Spinner,
  Text,
  useDisclosure
} from '@chakra-ui/react';
import { debounce } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWarning } from '@fortawesome/pro-regular-svg-icons';
import {
  Banner,
  Button,
  ButtonText,
  FormFeedback,
  Input,
  InputProps
} from '@morressier/alembic';
import { useMutation } from '@tanstack/react-query';

import {
  AffiliationCountryList,
  fetchRinggoldOrganization
} from 'src/api/queries';
import { FieldLabel } from '../../../../../../components/Field';
import { FieldError } from 'react-hook-form';
import { CountrySearchSelect } from 'src/components/User/CountrySearchSelect';

export type RinggoldOrg = {
  name: string;
  ringgoldId: string;
  topLevelOrganizationName?: string | null;
  organizationType?: string | null;
  formattedLocation?: string | null;
  primaryDomain?: string | null;
  country?: string | null;
  countryCode?: string | null;
  postCode?: string | null;
  city?: string | null;
};

interface Props {
  selectedCountry: AffiliationCountryList | null;
  selectedRinggoldId: string | undefined;
  onUpdate: (organization: Partial<RinggoldOrg>) => void;
  validateCOI?: (affiliation: string) => void;
  error?: Record<'affiliation' | 'country', FieldError | undefined>;
}

const RinggoldSearch = forwardRef<HTMLInputElement, InputProps & Props>(
  (
    {
      onUpdate,
      selectedCountry,
      selectedRinggoldId,
      error,
      validateCOI,
      ...props
    },
    ref
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const suppressNextClose = useRef(false);
    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    const [isUnverified, setIsUnverified] = useState(
      selectedRinggoldId === '' ? true : false
    );
    const { isOpen, onToggle, onClose } = useDisclosure();

    const {
      data: ringgoldOrgs,
      isLoading,
      mutateAsync: searchRinggoldOrgMutation
    } = useMutation({
      mutationFn: (searchTerm: string) =>
        fetchRinggoldOrganization(searchTerm, 0, 10)
    });

    const handleSearch = useCallback(
      debounce(async (value: string) => {
        // if value is less then 3 do not trigger the api
        // same validation is on backend side
        if (value.length < 3) {
          return;
        }

        if (!isOpen) onToggle();
        await searchRinggoldOrgMutation(value);
      }, 500),
      [onToggle, searchRinggoldOrgMutation]
    );

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value.trim();
      handleSearch(value);
      return props?.onChange?.(e);
    };

    const handleAddUnverifiedAffiliations = () => {
      onClosePopUp();
      setIsUnverified(true);
      const affiliationName = inputRef.current?.value?.trim() ?? '';
      if (affiliationName.length && validateCOI) {
        validateCOI(affiliationName);
      }
    };

    const renderAffiliationList = () => {
      // loading state
      if (isLoading) {
        return (
          <Flex flexDirection="column" gap={8} textAlign="center">
            <Box bgColor="neutralSecondaryFill" padding={8}>
              <Spinner fontSize={40} />
            </Box>
            <Flex flexDirection="column" gap={2}>
              <Text textStyle="headingTitle">Loading...</Text>
              <Text textStyle="textBody" paddingX={2}>
                We’re currently fetching Affiliation Results from the Ringgold
                database.
              </Text>
            </Flex>
          </Flex>
        );
      }

      // empty data state
      if (!isLoading && !ringgoldOrgs) {
        return (
          <Flex flexDirection="column" gap={8} textAlign="center">
            <Box bgColor="neutralSecondaryFill" padding={8}>
              <FontAwesomeIcon icon={faWarning} fontSize={40} />
            </Box>
            <Flex flexDirection="column" gap={2}>
              <Text textStyle="headingTitle">No Results</Text>
              <Text textStyle="textBody" paddingX={2}>
                This Affiliation is not available on the Ringgold database. You
                can add it as an unverified Affiliation.
              </Text>
              <Button
                variant="secondary"
                onClick={handleAddUnverifiedAffiliations}
                data-cy="ringgold-add-unverified-affiliation-button"
              >
                Add as unverified Affiliation
              </Button>
            </Flex>
          </Flex>
        );
      }

      return (
        <List
          padding={'0 !important'}
          display="flex"
          flexDirection="column"
          gap={4}
        >
          {ringgoldOrgs?.organizations.map(org => {
            return (
              <ListItem
                cursor="pointer"
                key={org.ringgoldId}
                onClick={() => {
                  onUpdate(org);
                  setIsUnverified(false);
                  onClosePopUp();
                }}
                data-cy={`ringgold-org-${org.ringgoldId}`}
              >
                <Text textStyle="headingItem">{org.name}</Text>
                <Text textStyle="textContext">
                  {org.topLevelOrganizationName}
                </Text>
                <Text textStyle="textContext">{org.formattedLocation}</Text>
              </ListItem>
            );
          })}
          <Box
            borderTopWidth="1px"
            borderStyle="solid"
            borderColor="neutralPrimaryBorder"
            paddingTop={4}
            paddingX={2}
          >
            <Banner
              variant="attention"
              heading="No matching results?"
              content={
                <Flex flexDirection={'column'} gap={2}>
                  <Text>
                    If your affiliation is not already present, please refine
                    your search criteria or add the affiliation manually.
                  </Text>
                  <Box marginStart={-1}>
                    <ButtonText
                      data-cy="ringgold-add-unverified-affiliation-button"
                      onClick={() => {
                        handleAddUnverifiedAffiliations();
                      }}
                    >
                      + Add Affiliation Manually
                    </ButtonText>
                  </Box>
                </Flex>
              }
            />
          </Box>
        </List>
      );
    };

    const onClosePopUp = () => {
      if (suppressNextClose.current) {
        suppressNextClose.current = false;
        return;
      }
      inputRef.current?.focus();
      onClose();

      setTimeout(() => {
        if (document.activeElement === inputRef.current) {
          (document.activeElement as HTMLElement)?.blur();
        }
      }, 0);
    };

    useEffect(() => {
      const handleOutsideClick = (e: MouseEvent) => {
        if (
          isOpen &&
          wrapperRef.current &&
          !wrapperRef.current.contains(e.target as Node)
        ) {
          onClose();
        }
      };
      document.addEventListener('mousedown', handleOutsideClick);
      return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, [isOpen, onClose]);

    return (
      <Flex flexDirection="column" gap={4}>
        <Box ref={wrapperRef}>
        <Popover
          returnFocusOnClose={true}
          isOpen={isOpen}
          onClose={onClosePopUp}
          placement="bottom"
          closeOnBlur={false}
          matchWidth
          autoFocus={false}
        >
          <PopoverTrigger>
            <Input
              ref={inputRef}
              {...props}
              onChange={handleChange}
              onFocus={handleChange}
              onMouseDown={() => {
                if (isOpen) suppressNextClose.current = true;
              }}
              data-cy="ringgold-search-input"
              autoComplete="off"
            />
          </PopoverTrigger>
          <PopoverContent
            minW={{ base: '100%' }}
            paddingY={4}
            paddingX={2}
            maxHeight={isLoading || !ringgoldOrgs ? 'none' : '264px'}
            overflowY={isLoading || !ringgoldOrgs ? 'unset' : 'auto'}
          >
            <PopoverBody padding={0}>{renderAffiliationList()}</PopoverBody>
          </PopoverContent>
        </Popover>
        </Box>
        {error?.affiliation ? (
          <FormFeedback variant="error">Affiliation is required</FormFeedback>
        ) : null}
        {isUnverified && (
          <Flex flexDirection="column" gap={2}>
            <FieldLabel label="Affiliated Country" isRequired subText="" />
            <CountrySearchSelect
              value={selectedCountry}
              onChange={onUpdate}
              isInvalid={!!error?.country}
            />
            {error?.country ? (
              <FormFeedback variant="error">
                Affiliated Country is required
              </FormFeedback>
            ) : null}
          </Flex>
        )}
      </Flex>
    );
  }
);

export default RinggoldSearch;
